import { apiBaseUrl } from './api-url.js';
import { currentAccessToken } from './auth.js';
import { ledger } from './browser.js';

/**
 * Downloading the Guardrail 3 export from the browser.
 *
 * WHY THIS FILE EXISTS AT ALL — and why there was no export button until 0.1.19. The
 * route is a plain authenticated GET, which sounds like `<a href="/export/catalog.csv">`
 * … except a link cannot carry an `Authorization: Bearer` header, so a clicked link
 * gets a 401 and a browser-rendered JSON error page. The fetch-then-blob dance below is
 * what turns an authenticated route into a downloaded file. (The route stays curl-able
 * and bookmark-able for anyone holding a token — that property is the guarantee and it
 * is untouched; this is only how the APP redeems it.)
 *
 * FAILURES ARE NEVER SILENT (G10): every path returns a message the caller renders and
 * writes a ledger entry, because a download that does not happen is otherwise
 * indistinguishable from a browser being slow.
 */

// Hoisted to api-url.ts in 0.1.209 - this was one of four copies of the same fact.
const API_URL = apiBaseUrl();
const EXPORT_TIMEOUT_MS = 90_000;
const REPORT_TIMEOUT_MS = 30_000;
const PACKING_LIST_TIMEOUT_MS = 60_000;

/**
 * WHAT THE EXPORT IS DOING RIGHT NOW — maintainer request, 7 Aug 2026: *"I need a visual
 * progress for when I export my whole catalogue."*
 *
 * ⚠️ TWO PHASES, BECAUSE THE WAIT HAS TWO PARTS AND ONLY ONE OF THEM CAN BE MEASURED.
 *
 * `export-route.ts` builds the entire CSV server-side and then calls `reply.send(result.csv)`
 * — the streamed pipeline is deliberately unbuilt (`ci/pending-checks.md`: *"~0.5 MB at 1,940
 * items does not justify it"*). So nothing arrives at all until the server has finished
 * assembling the whole thing.
 *
 * **A single percentage across that wait would be a lie**: it would sit at 0% for the long
 * part and then jump to 100%. A progress bar that does that is worse than no progress bar,
 * because it teaches people the number means nothing.
 *
 * So `preparing` reports ELAPSED TIME, which is true and useful ("it has been going 14
 * seconds, it has not hung"), and `downloading` reports a real byte percentage, which only
 * exists once bytes exist. **Whichever phase turns out to dominate, the screen is honest about
 * it** — and the split is itself the measurement nobody has taken.
 */
export type ExportProgress =
  | { phase: 'preparing'; elapsedMs: number }
  | { phase: 'downloading'; receivedBytes: number; totalBytes: number | null; elapsedMs: number }
  | { phase: 'saving'; elapsedMs: number };

export interface ExportOutcome {
  ok: boolean;
  /** User-facing, always set — success lines name the file and the count. */
  message: string;
}

function filenameFrom(disposition: string | null, fallback: string): string {
  const match = /filename="([^"]+)"/.exec(disposition ?? '');
  return match?.[1] ?? fallback;
}

/**
 * @param itemIds omit for the FULL catalogue export (Guardrail 3); pass a selection
 * for a hand-picked subset. The two differ in HTTP method, filename and headers —
 * see apps/api/src/http/export-route.ts.
 */
export async function downloadExport(
  itemIds?: string[],
  onProgress?: (progress: ExportProgress) => void,
): Promise<ExportOutcome> {
  if (!API_URL) return { ok: false, message: 'This build has no API configured.' };
  const selective = itemIds !== undefined;

  const token = await currentAccessToken();
  if (!token) {
    return { ok: false, message: 'Your session has expired. Sign in again, then export.' };
  }

  const startedAt = Date.now();
  const report = (progress: ExportProgress): void => {
    // Guarded: a caller that passes nothing must cost nothing, and a throwing callback must
    // not take the export down with it — the export is the one thing Guardrail 3 says always
    // works.
    try {
      onProgress?.(progress);
    } catch {
      /* a broken indicator is not a reason to fail a download */
    }
  };

  /**
   * ⚠️ THE TICKER IS WHAT MAKES "PREPARING" HONEST. Without it the screen would show one
   * static line for however long the server takes, which is indistinguishable from a hang —
   * the exact thing somebody exporting 1,940 items on switch day needs to be able to tell
   * apart. Cleared in `finally` so a failure cannot leave it running.
   */
  let ticker: ReturnType<typeof setInterval> | undefined = setInterval(() => {
    report({ phase: 'preparing', elapsedMs: Date.now() - startedAt });
  }, 250);
  const stopTicking = (): void => {
    if (ticker !== undefined) clearInterval(ticker);
    ticker = undefined;
  };
  report({ phase: 'preparing', elapsedMs: 0 });

  try {
    const response = await fetch(`${API_URL}/export/catalog.csv`, {
      method: selective ? 'POST' : 'GET',
      headers: {
        authorization: `Bearer ${token}`,
        ...(selective ? { 'content-type': 'application/json' } : {}),
      },
      ...(selective ? { body: JSON.stringify({ itemIds }) } : {}),
      signal: AbortSignal.timeout(EXPORT_TIMEOUT_MS),
    });

    // Headers are in, so the server has finished building — from here the wait is transfer.
    stopTicking();

    if (!response.ok) {
      // The route answers failures as JSON with a `reason` written for a human; use it
      // rather than inventing a message from the status code.
      let reason = `The export failed (HTTP ${String(response.status)}).`;
      try {
        const body = (await response.json()) as { reason?: unknown };
        if (typeof body.reason === 'string') reason = body.reason;
      } catch {
        /* not JSON — keep the status-based line */
      }
      ledger.record(
        Object.assign(new Error(reason), { status: response.status, name: 'ExportRefused' }),
        selective ? 'export:selection' : 'export:full',
        { notified: true },
      );
      return { ok: false, message: reason };
    }

    /**
     * READ THE BODY IN CHUNKS so the percentage is a real one.
     *
     * ⚠️ `content-length` MAY BE ABSENT — a proxy, or a compressed response, and Fastify's own
     * value describes the compressed bytes while the reader counts decompressed ones. So
     * `totalBytes` is nullable and the screen falls back to "N kB so far" rather than
     * inventing a denominator.
     *
     * ⚠️ AND `response.body` MAY BE NULL. Not every environment gives a readable stream — the
     * jsdom tests are one — so the whole thing degrades to `response.blob()`, which is exactly
     * what this function did before progress existed. **The export working matters more than
     * the export being watchable** (Guardrail 3).
     */
    const totalHeader = response.headers.get('content-length');
    const totalBytes = totalHeader === null ? null : Number(totalHeader);
    let blob: Blob;
    if (response.body === null) {
      blob = await response.blob();
    } else {
      const reader = response.body.getReader();
      const chunks: Uint8Array[] = [];
      let received = 0;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.byteLength;
        report({
          phase: 'downloading',
          receivedBytes: received,
          totalBytes: totalBytes !== null && Number.isFinite(totalBytes) ? totalBytes : null,
          elapsedMs: Date.now() - startedAt,
        });
      }
      blob = new Blob(chunks as BlobPart[], { type: 'text/csv' });
    }
    report({ phase: 'saving', elapsedMs: Date.now() - startedAt });

    const name = filenameFrom(
      response.headers.get('content-disposition'),
      selective ? 'mononoke-selection.csv' : 'mononoke-catalogue.csv',
    );
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name;
    anchor.click();
    // Revoked on the next tick, not immediately: Safari has been observed to cancel an
    // in-flight download when the object URL disappears in the same frame as the click.
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 0);

    // The server's own counts, echoed back — not the client's belief about them.
    const count = response.headers.get('X-Mononoke-Item-Count') ?? '?';
    const missing = Number(response.headers.get('X-Mononoke-Selection-Missing') ?? '0');
    const missingNote =
      missing > 0 ? ` (${String(missing)} selected item${missing === 1 ? '' : 's'} no longer exist and were skipped)` : '';
    return {
      ok: true,
      message: selective
        ? `Downloaded ${name} — ${count} item${count === '1' ? '' : 's'}${missingNote}.`
        : `Downloaded ${name} — the full catalogue, ${count} items.`,
    };
  } catch (error) {
    const entry = ledger.record(error, selective ? 'export:selection' : 'export:full', { notified: true });
    return { ok: false, message: entry.userMessage };
  } finally {
    // ⚠️ HERE, NOT ONLY ON THE HAPPY PATH. A timeout, an aborted fetch or a thrown reader all
    // leave this function by a route that never reaches `stopTicking()` above — and an
    // interval nobody clears goes on reporting progress for a download that ended, forever.
    stopTicking();
  }
}

/**
 * Downloading a REPORT (Phase 3 step 1). Same fetch-then-blob dance and the same
 * reason: an authenticated route cannot be an `<a href>`. Kept in this file rather
 * than beside the reports screen so there is ONE place that knows how this app turns
 * an authenticated CSV route into a file on disk — the export taught that lesson and
 * a second copy would drift from it.
 *
 * Shorter timeout than the catalogue export: a report is at most a few thousand rows,
 * and 90 seconds of a spinner for something that should take a second teaches people
 * the app is broken.
 */
export async function downloadReport(key: string): Promise<ExportOutcome & { filename?: string }> {
  if (!API_URL) return { ok: false, message: 'This build has no API configured.' };

  const token = await currentAccessToken();
  if (!token) {
    return { ok: false, message: 'Your session has expired. Sign in again, then download.' };
  }

  try {
    const response = await fetch(`${API_URL}/reports/${encodeURIComponent(key)}.csv`, {
      headers: { authorization: `Bearer ${token}` },
      signal: AbortSignal.timeout(REPORT_TIMEOUT_MS),
    });

    if (!response.ok) {
      let reason = `The report failed (HTTP ${String(response.status)}).`;
      try {
        const body = (await response.json()) as { reason?: unknown };
        if (typeof body.reason === 'string') reason = body.reason;
      } catch {
        /* not JSON — keep the status-based line */
      }
      ledger.record(Object.assign(new Error(reason), { status: response.status, name: 'ReportRefused' }), 'report:csv', {
        notified: true,
      });
      return { ok: false, message: reason };
    }

    const blob = await response.blob();
    const name = filenameFrom(response.headers.get('content-disposition'), `mononoke-${key}.csv`);
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name;
    anchor.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 0);

    // The server's own row count, echoed back rather than recomputed from the blob.
    const rows = response.headers.get('X-Mononoke-Report-Rows') ?? '?';
    return { ok: true, filename: name, message: `Downloaded ${name} — ${rows} row(s).` };
  } catch (error) {
    const entry = ledger.record(error, 'report:csv', { notified: true });
    return { ok: false, message: entry.userMessage };
  }
}

/**
 * Downloading a PACKING LIST (goods-flow spec §7 G5). Third caller of the same
 * fetch-then-blob dance, and it stays in this file for the reason the report gave: ONE
 * place knows how this app turns an authenticated route into a file on disk.
 *
 * The timeout sits between the other two. A packing list is at most a few dozen items,
 * but it embeds a full-size photograph for each of them, so it is slower than a report
 * and nowhere near a 1,940-row catalogue export.
 */
export async function downloadPackingList(bookingId: string): Promise<ExportOutcome & { filename?: string }> {
  if (!API_URL) return { ok: false, message: 'This build has no API configured.' };

  const token = await currentAccessToken();
  if (!token) {
    return { ok: false, message: 'Your session has expired. Sign in again, then download.' };
  }

  try {
    const response = await fetch(`${API_URL}/booking/${encodeURIComponent(bookingId)}/packing-list.pdf`, {
      headers: { authorization: `Bearer ${token}` },
      signal: AbortSignal.timeout(PACKING_LIST_TIMEOUT_MS),
    });

    if (!response.ok) {
      // The route answers failures as JSON with a `reason` written for a human — "packing
      // lists are for admin and staff" is more use than "HTTP 403".
      let reason = `The packing list failed (HTTP ${String(response.status)}).`;
      try {
        const body = (await response.json()) as { reason?: unknown };
        if (typeof body.reason === 'string') reason = body.reason;
      } catch {
        /* not JSON — keep the status-based line */
      }
      ledger.record(
        Object.assign(new Error(reason), { status: response.status, name: 'PackingListRefused' }),
        'packing-list',
        { notified: true },
      );
      return { ok: false, message: reason };
    }

    const blob = await response.blob();
    const name = filenameFrom(response.headers.get('content-disposition'), 'packing-list.pdf');
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name;
    anchor.click();
    // Next tick, not this one: Safari cancels an in-flight download when the object URL
    // disappears in the same frame as the click (the export's own scar).
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 0);

    // The server's count, not the client's belief about it — "it downloaded" and "it is
    // complete" are different claims and the person holding it can check the second.
    const count = response.headers.get('X-Mononoke-Item-Count') ?? '?';
    return { ok: true, filename: name, message: `Downloaded ${name} — ${count} piece(s).` };
  } catch (error) {
    const entry = ledger.record(error, 'packing-list', { notified: true });
    return { ok: false, message: entry.userMessage };
  }
}
